import { inject, Injectable, signal } from '@angular/core';
import { LoginData } from '../../shared/model/login-data';
import { catchError, map, Observable, of, tap } from 'rxjs';
import { User } from '../../shared/model/user';
import { HttpClient, HttpParams } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { ApiResponse } from '../../shared/model/api-response';
import { RegisterData } from '../../shared/model/register-data';
import { Router } from '@angular/router';
import { UserService } from '../../shared/services/user.service';
import { TelemetryService } from '../services/telemetry.service';

export const AUTH_TOKEN_STORAGE_KEY = 'gc.auth.token';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  httpClient: HttpClient = inject(HttpClient);
  router: Router = inject(Router);
  userService: UserService = inject(UserService);
  telemetryService: TelemetryService = inject(TelemetryService);

  private readonly logged = signal(false);
  private readonly currentUser = signal<User | undefined>(undefined);

  public makeLogin(loginData: LoginData): Observable<ApiResponse<string>> {
    return this.httpClient.post<ApiResponse<string>>(`${environment.apiUrl}/v1/auth/login`, loginData);
  }

  public makeGoogleLogin(idToken: string): Observable<ApiResponse<string>> {
    return this.httpClient.post<ApiResponse<string>>(`${environment.apiUrl}/v1/auth/google`, { idToken });
  }

  public makeSignup(registerData: RegisterData): Observable<ApiResponse<User>> {
    return this.httpClient.post<ApiResponse<User>>(
      `${environment.apiUrl}/v1/auth/signup`,
      registerData,
    );
  }

  public makeLoginByToken(token: string): Observable<ApiResponse<User>> {
    const params = new HttpParams().set('token', token);
    return this.httpClient.get<ApiResponse<User>>(`${environment.apiUrl}/v1/auth/login`, { params });
  }

  public verifyEmail(token: string): Observable<ApiResponse<string>> {
    const params = new HttpParams().set('token', token);
    return this.httpClient.get<ApiResponse<string>>(`${environment.apiUrl}/v1/auth/verify-email`, {
      params,
    });
  }

  public requestPasswordReset(email: string, frontendBaseUrl: string = window.location.origin): Observable<ApiResponse<string>> {
    return this.httpClient.post<ApiResponse<string>>(`${environment.apiUrl}/v1/auth/forgot-password`, {
      email,
      frontendBaseUrl,
    });
  }

  public resetPassword(token: string, newPassword: string): Observable<ApiResponse<string>> {
    return this.httpClient.put<ApiResponse<string>>(`${environment.apiUrl}/v1/auth/reset-password`, {
      token,
      newPassword,
    });
  }

  public restoreSession(): Promise<void> {
    this.clearLegacyAuthStorage();
    const token = this.getToken();
    if (!token) {
      return Promise.resolve();
    }
    this.logged.set(true);
    return new Promise((resolve) => {
      this.userService
        .getMyUserData()
        .pipe(
          tap((response) => {
          if (response.success && response.data && 'userType' in response.data) {
            this.currentUser.set(response.data);
            this.telemetryService.identify(response.data.email || response.data.userName, {
              id: response.data.id,
              username: response.data.userName,
              userType: response.data.userType
            });
          } else {
            this.clearSessionState();
          }
          }),
          catchError(() => {
            this.clearSessionState();
            return of(null);
          }),
        )
        .subscribe({
          complete: () => resolve(),
        });
    });
  }

  public completeLogin(token: string, rememberMe: boolean = false): Observable<User | undefined> {
    this.clearSessionState();
    if (rememberMe) {
      localStorage.setItem(AUTH_TOKEN_STORAGE_KEY, token);
    } else {
      sessionStorage.setItem(AUTH_TOKEN_STORAGE_KEY, token);
    }
    this.logged.set(true);
    return this.userService.getMyUserData().pipe(
      tap((response) => {
        if (response.success && response.data) {
          this.setCurrentUser(response.data);
        }
      }),
      map((response): User | undefined => {
        if (response.success && response.data) {
          return response.data;
        }
        return undefined;
      }),
    );
  }

  public setCurrentUser(user: User): void {
    this.currentUser.set(user);
    this.logged.set(true);
    this.telemetryService.identify(user.email || user.userName, {
      id: user.id,
      username: user.userName,
      userType: user.userType
    });
  }

  public logout(): void {
    this.clearSessionState();
    this.router.navigate(['/login'], { replaceUrl: true });
  }

  public isLogged(): boolean {
    return this.logged();
  }

  public getToken(): string | null {
    return sessionStorage.getItem(AUTH_TOKEN_STORAGE_KEY) || localStorage.getItem(AUTH_TOKEN_STORAGE_KEY);
  }

  public getCurrentUser(): User | undefined {
    return this.currentUser();
  }

  public isAssociation(): boolean {
    return this.getCurrentUser()?.userType === 'ASSOCIATION';
  }

  public isAdmin(): boolean {
    return this.getCurrentUser()?.userType === 'ADMINISTRATOR';
  }

  private clearSessionState(): void {
    sessionStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
    localStorage.removeItem(AUTH_TOKEN_STORAGE_KEY);
    this.logged.set(false);
    this.currentUser.set(undefined);
    this.clearLegacyAuthStorage();
  }

  private clearLegacyAuthStorage(): void {
    localStorage.removeItem('logged');
    localStorage.removeItem('token');
    localStorage.removeItem('userData');
  }
}
